import {Button, CrossIcon, DownloadIcon, List, ListItem, OpenIcon, toggleHtmlClass} from '@kit';

import React, {useCallback, useEffect, useRef} from 'react';

import {Modal} from '../modal';
import {Keys, throttle} from '../utils';
import {useGalleryContext} from './gallery-context';

import css from './gallery.module.css';

export const GalleryViewer: React.FC = () => {
	const ctx = useGalleryContext();
	const images = ctx?.images ?? [];
	const openId = ctx?.openId;
	const close = ctx?.close;
	const navigateTo = ctx?.navigateTo;

	const currentIndex = images.findIndex((i) => i.id === openId);
	const current = images[currentIndex] ?? null;

	const wrapperRef = useRef<HTMLDivElement>(null);
	const galleryRef = useRef<HTMLDivElement>(null);

	const handleNext = useCallback(() => {
		const next = images[currentIndex + 1 >= images.length ? 0 : currentIndex + 1];
		if (next && navigateTo) navigateTo(next.id);
	}, [currentIndex, images, navigateTo]);

	const handlePrev = useCallback(() => {
		const prev = images[currentIndex === 0 ? images.length - 1 : currentIndex - 1];
		if (prev && navigateTo) navigateTo(prev.id);
	}, [currentIndex, images, navigateTo]);

	useEffect(() => {
		if (!openId) return;
		toggleHtmlClass(true);
		return () => toggleHtmlClass(false);
	}, [openId]);

	useEffect(() => {
		if (!wrapperRef.current || currentIndex < 0) return;
		wrapperRef.current.scrollLeft = currentIndex * wrapperRef.current.clientWidth;
	}, [currentIndex]);

	useEffect(() => {
		if (!galleryRef.current || currentIndex < 0) return;
		const gallery = galleryRef.current;
		const selectedEl = gallery.children[currentIndex] as HTMLElement;
		if (!selectedEl) return;
		gallery.scrollTo({
			left: selectedEl.offsetLeft - gallery.clientWidth / 2 + selectedEl.offsetWidth / 2,
			behavior: 'smooth',
		});
	}, [currentIndex]);

	useEffect(() => {
		// стрелки листают галерею только пока она открыта: иначе они бы открывали её из любой формы
		if (!openId) return;

		function checkKey(e: KeyboardEvent) {
			if (e.keyCode === Keys.ESCAPE && close) close();
			if (e.keyCode === Keys.LEFT_ARROW) handlePrev();
			if (e.keyCode === Keys.RIGHT_ARROW) handleNext();
		}
		document.addEventListener('keyup', checkKey);
		return () => document.removeEventListener('keyup', checkKey);
	}, [openId, close, handlePrev, handleNext]);

	const handleScroll = useCallback(
		throttle(() => {
			if (!wrapperRef.current || !navigateTo) return;
			const scrollLeft = wrapperRef.current.scrollLeft;
			const imageElements = wrapperRef.current.children;
			for (let i = 0; i < imageElements.length; i++) {
				const el = imageElements.item(i) as HTMLDivElement;
				if (Math.round(el?.offsetLeft) - Math.round(scrollLeft) === 0) {
					const id = el.dataset.id;
					if (id && id !== openId) navigateTo(id);
					break;
				}
			}
		}, 200),
		[openId, navigateTo],
	);

	// якорь может указывать на картинку, которой на этой странице нет: пустую галерею не открываем
	if (!openId || !current) return null;

	return (
		<Modal>
			<div className={css.wrapper}>
				<div className={css.header}>
					{current && (
						<>
							<Button
								appearance="subtle"
								icon={<OpenIcon />}
								href={current.url}
								target="_blank"
								rel="noreferrer"
								aria-label="Открыть оригинал"
								title="Открыть оригинал"
							/>
							<Button
								appearance="subtle"
								icon={<DownloadIcon />}
								href={current.url}
								download
								aria-label="Скачать"
								title="Скачать"
							/>
						</>
					)}
					<Button appearance="subtle" icon={<CrossIcon />} onClick={close} aria-label="Закрыть" />
				</div>

				<div className={css.stage}>
					<div className={css.images} ref={wrapperRef} onScroll={handleScroll}>
						{images.map((image) => (
							<div
									className={css.imageWrapper}
									data-id={image.id}
									key={image.id}
									onClick={(e) => e.target === e.currentTarget && close?.()}
								>
								<img className={css.image} src={image.url} alt={image.description} />
							</div>
						))}
					</div>
					{(current?.credits || current?.description) && (
						<div className={css.title}>
							{current.credits && (
								<footer>
									<small>{current.credits}</small>
								</footer>
							)}
							{current.description}
						</div>
					)}
				</div>
				<List
					forwardRef={galleryRef}
					horizontal
					navigationMode="items"
					selectMode="single"
					selectedItems={openId ? [openId] : []}
					className={css.gallery}
				>
					{images.map((image) => (
						<ListItem
							key={image.id}
							value={image.id}
							onAction={() => navigateTo?.(image.id)}
							className={css.galleryItem}
							buttonClassName={css.galleryItemButton}
							selectedClassName={css.galleryItemSelected}
						>
							<img src={image.preview} alt={image.description || ''} width={100} height={80} />
						</ListItem>
					))}
				</List>
			</div>
		</Modal>
	);
};
